import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const Blogs = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    const unsubscribe = db
      .collection("articles")
      .orderBy("createdAt", "desc")
      .onSnapshot((snapshot) => {
        setArticles(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      });
    return () => unsubscribe();
  }, []);

  const groupedArticles = {
    مدني: articles.filter((a) => a.section === "مدني"),
    جنائي: articles.filter((a) => a.section === "جنائي"),
    إداري: articles.filter((a) => a.section === "إداري"),
    تجاري: articles.filter((a) => a.section === "تجاري"),
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.25 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
  };

  return (
    <section className="blogs" dir="rtl">
      <style>{`
        :root {
          --accent: #1f6f3a;
          --muted: #6b7a6b;
          --card-bg: #fff;
        }

        .blogs {
          padding: 100px 20px;
          background: linear-gradient(180deg, rgba(47,63,47,0.03), rgba(255,255,255,0));
          font-family: "Noto Sans Arabic", Arial, sans-serif;
        }

        .blogs__container {
          max-width: 1100px;
          margin: 0 auto;
        }

        .blogs__title {
          text-align: center;
          font-size: 30px;
          font-weight: 700;
          color: #0b2e13;
          position: relative;
          display: inline-block;
          margin: 0 auto 60px;
        }

        .blogs__title::before,
        .blogs__title::after {
          content: '';
          position: absolute;
          top: 50%;
          width: 30px;
          height: 2px;
          background-color: var(--accent);
        }

        .blogs__title::before {
          right: 100%;
          margin-right: 15px;
        }

        .blogs__title::after {
          left: 100%;
          margin-left: 15px;
        }

        .section-block {
          margin-bottom: 80px;
        }

        .section-block h4 {
          font-size: 20px;
          font-weight: 600;
          color: var(--accent);
          margin-bottom: 22px;
          text-align: right;
          border-bottom: 2px solid rgba(31,111,58,0.2);
          padding-bottom: 6px;
          display: inline-block;
        }

        .articles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
        }

        .article-card {
          background: var(--card-bg);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 6px 18px rgba(0,0,0,0.06);
          transition: all 0.25s ease;
          display: flex;
          flex-direction: column;
        }

        .article-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 26px rgba(15,23,15,0.08);
        }

        .article-image {
          width: 100%;
          height: 180px;
          object-fit: cover;
        }

        .article-content {
          padding: 18px;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .article-title {
          font-size: 17px;
          font-weight: 600;
          color: #163b20;
          margin: 0 0 12px;
          line-height: 1.4;
        }

        .article-btn {
          align-self: center;
          background: var(--accent);
          color: #fff;
          padding: 8px 14px;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 6px 14px rgba(31,111,58,0.15);
          transition: all 0.2s ease;
        }

        .article-btn:hover {
          transform: scale(1.05);
        }

        .no-articles {
          grid-column: 1/-1;
          text-align: center;
          color: var(--muted);
          font-size: 15px;
        }
      `}</style>

      <div className="blogs__container">
        <motion.div
          className="title-wrapper"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          style={{ textAlign: "center" }}
        >
          <h2 className="blogs__title">المقالات القانونية</h2>
        </motion.div>

        {Object.keys(groupedArticles).map((section, idx) => (
          <motion.div
            key={section}
            className="section-block"
            variants={itemVariants}
            initial="hidden"
            whileInView="show"
            transition={{ delay: idx * 0.2 }}
            viewport={{ once: true }}
          >
            <h4>القسم {section}</h4>

            <motion.div
              className="articles-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {groupedArticles[section].length > 0 ? (
                groupedArticles[section].map((article) => (
                  <motion.div
                    key={article.id}
                    className="article-card"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                  >
                    <motion.img
                      src={article.imageUrl}
                      alt={article.title}
                      className="article-image"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    />
                    <div className="article-content">
                      <h3 className="article-title">{article.title}</h3>
                      <Link
                        to={`/article/${article.id}`}
                        className="article-btn"
                      >
                        عرض المقالة
                      </Link>
                    </div>
                  </motion.div>
                ))
              ) : (
                <p className="no-articles">
                  لا توجد مقالات في هذا القسم حالياً
                </p>
              )}
            </motion.div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Blogs;
